/**
 * Debrief Tab Component
 * Displays mission report and evidence
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as FileIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import type { MissionDetail, MissionEvidence } from '@/types/mission';
import { EvidencePreviewDialog } from '../EvidencePreviewDialog';

interface DebriefTabProps {
  mission: MissionDetail;
}

export const DebriefTab: React.FC<DebriefTabProps> = ({ mission }) => {
  const theme = useTheme();
  const [previewEvidence, setPreviewEvidence] = useState<MissionEvidence | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Check if content is HTML
  const isHtmlContent = (content: string): boolean => {
    if (!content) return false;
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;
    return htmlRegex.test(content);
  };

  // Render content (HTML or plain text)
  const renderContent = (content: string) => {
    if (isHtmlContent(content)) {
      return (
        <Box
          sx={{
            color: theme.palette.text.primary,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1.6,
            '& p': {
              marginBottom: theme.spacing(1),
              '&:last-child': {
                marginBottom: 0,
              },
            },
            '& ul, & ol': {
              marginLeft: theme.spacing(2),
              marginBottom: theme.spacing(1),
            },
            '& li': {
              marginBottom: theme.spacing(0.5),
            },
            '& strong, & b': {
              fontWeight: 600,
            },
          }}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    return (
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.primary,
          fontSize: theme.typography.body2.fontSize,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}
      >
        {content}
      </Typography>
    );
  };

  const getEvidenceIcon = (type: string) => {
    switch (type) {
      case 'photo':
        return <ImageIcon />;
      case 'video':
        return <VideoIcon />;
      case 'file':
        return <FileIcon />;
      default:
        return <FileIcon />;
    }
  };

  const handlePreview = (evidence: MissionEvidence) => {
    setPreviewEvidence(evidence);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewEvidence(null);
  };

  const handleDownload = (evidence: MissionEvidence) => {
    if (evidence.url) {
      window.open(evidence.url, '_blank');
    }
  };

  // Check if there's any debrief content
  const hasDebriefContent = mission.reportWeThink || 
    mission.reportConfidence !== undefined || 
    mission.reportHereWhy;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
        {/* Debrief Content */}
        {hasDebriefContent ? (
          <Box>
            {/* We Think Section */}
            {mission.reportWeThink && (
              <Box sx={{ marginBottom: theme.spacing(3) }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(1),
                    fontSize: theme.typography.caption.fontSize,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  WE THINK
                </Typography>
                {renderContent(mission.reportWeThink)}
              </Box>
            )}

            {/* Confidence Section */}
            {mission.reportConfidence !== undefined && (
              <Box sx={{ marginBottom: theme.spacing(3) }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(1),
                    fontSize: theme.typography.caption.fontSize,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  CONFIDENCE
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: 1.6,
                  }}
                >
                  {typeof mission.reportConfidence === 'string' 
                    ? mission.reportConfidence 
                    : `${mission.reportConfidence}%`}
                </Typography>
              </Box>
            )}

            {/* Here's Why Section */}
            {mission.reportHereWhy && (
              <Box sx={{ marginBottom: theme.spacing(3) }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(1),
                    fontSize: theme.typography.caption.fontSize,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  HERE'S WHY
                </Typography>
                {renderContent(mission.reportHereWhy)}
              </Box>
            )}

            {/* Submitted Timestamp */}
            {(mission.report?.createdAtText || mission.report?.createdAt) && (
              <Box sx={{ marginTop: theme.spacing(2) }}>
                <Typography
                  variant="caption"
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                >
                  Submitted: {mission.report.createdAtText || 'N/A'}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: theme.spacing(8),
            textAlign: 'center',
          }}>
            <FileIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, marginBottom: theme.spacing(2) }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(1),
              }}
            >
              No Debrief
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              No debrief has been submitted for this mission yet.
            </Typography>
          </Box>
        )}

        {/* Evidence */}
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              marginBottom: theme.spacing(2),
              color: theme.palette.text.primary,
              fontSize: '0.875rem',
            }}
          >
            Evidence ({mission.evidence?.length || 0})
          </Typography>

          {!mission.evidence || mission.evidence.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              No evidence available.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {mission.evidence.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card
                    sx={{
                      position: 'relative',
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: theme.shadows[4],
                      },
                    }}
                    onClick={() => handlePreview(item)}
                  >
                    {item.type === 'photo' && item.thumbnailUrl ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={item.thumbnailUrl}
                        alt={item.fileName || 'Evidence'}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (item.url && target.src !== item.url) {
                            target.src = item.url;
                          } else {
                            target.src = `https://via.placeholder.com/400x300/333333/ffffff?text=Image`;
                          }
                        }}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : item.type === 'photo' && item.url ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={item.url}
                        alt={item.fileName || 'Evidence'}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://via.placeholder.com/400x300/333333/ffffff?text=Image`;
                        }}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: theme.palette.background.default,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        <Box sx={{ fontSize: 48 }}>{getEvidenceIcon(item.type)}</Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        position: 'absolute',
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                        display: 'flex',
                        gap: theme.spacing(0.5),
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreview(item);
                          }}
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: theme.palette.common.white,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            },
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(item);
                          }}
                          sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: theme.palette.common.white,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            },
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ padding: theme.spacing(1.5) }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: theme.palette.text.primary,
                          marginBottom: theme.spacing(0.5),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem',
                        }}
                      >
                        {item.fileName || `${item.type} evidence`}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ 
                          color: theme.palette.text.secondary, 
                          display: 'block',
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.fileSize && formatFileSize(item.fileSize)}
                        {item.uploadedAtText && ` • ${item.uploadedAtText}`}
                      </Typography>
                      {item.uploadedBy && (
                        <Typography
                          variant="caption"
                          sx={{ 
                            color: theme.palette.text.secondary, 
                            display: 'block',
                            fontSize: '0.75rem',
                          }}
                        >
                          Uploaded by {item.uploadedBy}
                        </Typography>
                      )}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      <EvidencePreviewDialog
        open={previewOpen}
        evidence={previewEvidence}
        onClose={handleClosePreview}
        onDownload={handleDownload}
      />
    </>
  );
};
